<template>
  <!-- Only rendered once the backend says a log is available for this app. -->
  <div v-if="available" class="vds-deploylog">
    <div class="d-flex align-center mb-1">
      <v-icon x-small class="mr-2 vds-deploylog__icon" aria-hidden="true">mdi-text-box-outline</v-icon>
      <span class="text-caption mr-auto vds-deploylog__label">Install log</span>
      <span v-if="loadingLog" class="text-caption vds-deploylog__updating" aria-hidden="true">updating…</span>
    </div>
    <pre
      ref="log"
      class="vds-deploylog__body"
      tabindex="0"
      role="log"
      aria-live="polite"
      aria-label="Install log"
      :aria-busy="loadingLog ? 'true' : 'false'"
    >{{ displayContent || 'Waiting for install output…' }}</pre>
  </div>
</template>

<script>
import kvm from '@/api/kvm'

const POLL_MS = 3000

export default {
  name: 'KvmDeployLog',
  props: {
    entitlementId: { type: [String, Number], default: null },
    // Whether the app exposes the deploy log (capabilities.deploy_logs).
    capable: { type: Boolean, default: false },
    // Poll live while true (install in progress); when false we fetch once so a
    // finished/failed install still shows its final log.
    active: { type: Boolean, default: false },
  },
  data: () => ({
    available: false,
    complete: false,
    truncated: false,
    content: '',
    loadingLog: false,
    timer: null,
  }),
  computed: {
    displayContent () {
      if (!this.content) return ''
      return (this.truncated ? '… earlier output omitted …\n\n' : '') + this.content
    },
  },
  watch: {
    active (v) { v ? this.start() : this.stop() },
  },
  mounted () {
    this.start()
  },
  beforeDestroy () {
    this.stop()
  },
  methods: {
    start () {
      if (!this.capable || !this.entitlementId) return
      this.stop()
      this.tick()
    },
    stop () {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
    },
    async tick () {
      await this.fetchLog()
      // Keep polling only while the install is live and not yet complete.
      if (this.active && !this.complete) {
        this.timer = setTimeout(this.tick, POLL_MS)
      }
    },
    async fetchLog () {
      if (!this.capable || !this.entitlementId) return
      // Follow the tail only if the user is already near the bottom.
      const el = this.$refs.log
      const atBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 40)
      try {
        this.loadingLog = true
        const d = await kvm.getDeployLog(this.entitlementId)
        this.available = !!d.available
        this.truncated = !!d.truncated
        this.complete = !!d.complete
        this.content = d.content || ''
        if (atBottom) this.$nextTick(this.scrollToBottom)
      } catch (e) {
        // Transient (e.g. agent briefly unavailable) — keep what we have.
      } finally {
        this.loadingLog = false
      }
    },
    scrollToBottom () {
      const el = this.$refs.log
      if (el) el.scrollTop = el.scrollHeight
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Header chrome around the terminal adapts to light/dark; AA secondary text.
.vds-deploylog__icon,
.vds-deploylog__label,
.vds-deploylog__updating {
  @include kvm-secondary-text;
}

// The log itself is an intentionally dark "terminal" surface in both themes.
.vds-deploylog__body {
  background: #0d1117;
  color: #c9d1d9; // ≈ 12.6:1 on #0d1117 (AA/AAA)
  font-family: Menlo, Consolas, 'DejaVu Sans Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  max-height: 300px;
  max-width: 100%;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  &:focus-visible {
    @include kvm-focus-ring($dark: #6fd57f);
  }
}

// Keep the panel usable on small screens (no viewport overflow).
@media (max-width: $kvm-bp-mobile) {
  .vds-deploylog__body {
    max-height: 240px;
    font-size: 11px;
  }
}
</style>
